import { PHASE_HEALTH_FAULTS } from '../../constants';
import { getDroneIntervals } from '../../helpers/getDroneIntervals';
import { getHealthRings } from '../../helpers/getHealthRings';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import WarningIcon from 'components/icons/WarningIcon';

import RingGauge from 'pages/MaintenancePage/components/RingGauge';
import Sparkline from 'pages/MaintenancePage/components/Sparkline';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import DroneActivityLink from './components/DroneActivityLink';
import DroneCardDetails from './components/DroneCardDetails';
import DroneCardHeader from './components/DroneCardHeader';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const DroneCard: FunctionComponent<Props> = ({
    className = '',
    drone,
    isAdmin,
    onOpen,
    onSetAirworthy,
    onSetLifecycle,
    phase,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const labels = { motors: t.MOTORS_LABEL, props: t.PROPS_LABEL, pumps: t.PUMPS_LABEL };
    const showFaults = phase >= PHASE_HEALTH_FAULTS;
    const cardStateClass = drone.status === 'good' ? '' : styles[`is${drone.status}`];
    const warningStateClass = drone.status === 'critical' ? '' : styles.isCaution;
    const lastSyncStaleClass = drone.lastLogSync.stale ? styles.isStale : '';
    const droneIntervals = getDroneIntervals({ drone, serviceIntervals });
    const rings = getHealthRings({ drone, labels, serviceIntervals: droneIntervals });

    return (
        <VerticalContainer
            className={`${styles.droneCard} ${cardStateClass} ${className}`}
            onClick={() => onOpen(drone.id)}
            testId={testId}>
            <DroneCardHeader
                drone={drone}
                isAdmin={isAdmin}
                onSetAirworthy={onSetAirworthy}
                onSetLifecycle={onSetLifecycle}
                showStatus={showFaults}
            />

            <Wrapper className={`${styles.lastSync} ${lastSyncStaleClass}`}>
                {`${t.LOG_SYNCED} ${drone.lastLogSync.label}`}
            </Wrapper>

            <Wrapper className={styles.ringRow}>
                {rings.map(ring => (
                    <RingGauge
                        key={ring.label}
                        label={ring.label}
                        notConfiguredText={t.NOT_CONFIGURED}
                        serviceLife={ring.serviceLife}
                    />
                ))}
            </Wrapper>

            <DroneCardDetails drone={drone} />

            {showFaults && drone.topWarning && (
                <HorizontalContainer className={`${styles.topWarning} ${warningStateClass}`}>
                    <WarningIcon className={styles.warningIcon} />

                    <Wrapper className={styles.topWarningText}>{drone.topWarning}</Wrapper>
                </HorizontalContainer>
            )}

            {showFaults && drone.watching.length > 0 && (
                <VerticalContainer className={styles.watchList}>
                    {drone.watching.map(entry => (
                        <Wrapper className={styles.watchItem} key={entry}>
                            {entry}
                        </Wrapper>
                    ))}
                </VerticalContainer>
            )}

            {showFaults && <Sparkline label={t.FAULTS_30D} values={drone.faultHistory} />}

            <DroneActivityLink droneId={drone.id} />
        </VerticalContainer>
    );
};

export default DroneCard;
